// Provenance: repo:tds-reconciliation-portal:frontend/src/components/AliasPicker.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings
// (`party_id`, group ids); the ranking/corroboration maths, the two honest dividers, the
// tiered orderings and the plan-preview contract carry unchanged. The pure helpers stay
// exported by name — MatchWorkbench builds its surface out of the same NameColumn.
import { Fragment, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { useAliasCandidates, useAliasPlan, useCreateAliasFromRun } from '../api/aliases'
import { ApiError } from '../api/client'
import type { AliasCandidates, AliasPlan } from '../api/types'
import { withRunParam } from '../workspace/runContext'
import { Chip } from './badges'
import { Btn } from './Modal'
import { showToast } from './toast'

export type Side = '26as' | 'books'
export type Scope = 'orphans' | 'all'

export interface Ranking {
  distinctive: number
  band: string
  evidence: string[]
  only_common: boolean
  initials?: string | null
  amount_hits?: { n: number; money: string; amounts: string[] } | null
}

/** How much the corroborating signals lift a candidate in the ordering.
 *
 *  Measured on the predecessor's run 56: a two-entry rare-amount signature
 *  found BEML and VIDAL — correct pairs whose name score is near zero — so it
 *  must be able to overtake a merely `likely` name (0.35 > the likely/weak
 *  gap). One hit, or a short-form prefix alone, nudges without overtaking real
 *  name evidence: each is exactly the kind of signal that is right with a
 *  second witness and coincidental without one.
 */
export function corroborationBoost(r: Ranking): number {
  let boost = 0
  const n = r.amount_hits?.n ?? 0
  if (n >= 2) boost += 0.35
  else if (n === 1) boost += 0.1
  if (r.initials) boost += 0.1
  return boost
}

export interface PickerOption {
  name: string
  party: string | null
  /** the party's row id, when this name came from the party projection —
   *  what a link to Reconciliation needs */
  party_id: string | null
  state: string | null
  tds: string | null
  tan: string | null
  orphan: boolean
  /** ranking fields, present only once something HAS been picked */
  band?: string
  evidence?: string[]
  distinctive?: number
  /** the approved group that already claims this name — the alias applies from
   *  the NEXT run, so the name is still one-sided in this one */
  covered_by?: string | null
  /** short-form sentence and rare-amount ties, from the ranking */
  initials?: string | null
  amount_hits?: { n: number; money: string; amounts: string[] } | null
  /** distinctive + corroboration — what the ranked ordering actually uses */
  rank?: number
}

/** The options for one column.
 *
 *  `all` is the point of the redesign. The counterpart column used to be the
 *  orphans, which meant an orphan could only ever be joined to another orphan —
 *  and the alias worth making is usually the one that joins a books name to a
 *  party that is already matched and short. Every party in the run is offered,
 *  with its state, so that case can be expressed at all.
 */
export type Sort = 'value' | 'name'

export function sideOptions(
  data: AliasCandidates | undefined,
  side: Side,
  scope: Scope,
  query = '',
  ranked?: Record<string, Ranking>,
  sort: Sort = 'value',
): PickerOption[] {
  if (!data) return []
  const oneSided = side === '26as' ? data.one_sided_26as : data.one_sided_books
  const orphans = new Map(oneSided.map((o) => [o.name, o]))
  // Which group already claims each name — the RUN's map, and ONLY it.
  //
  // This was read off the one-sided entry, so a both-sided name could never be
  // marked: it is not in that list. A party joined by a manual alias rendered
  // in All parties as if it were free, where ticking it supersedes that alias
  // with nothing on the row to say so. The one-sided entries still carry
  // `covered_by`, derived server-side from this same computation, and this
  // column deliberately does not consult it — two fields that could disagree
  // about one fact is the drift this project keeps finding.
  const covered = side === '26as' ? data.covered_names_26as : data.covered_names_books
  const out: PickerOption[] = []
  const seen = new Set<string>()

  const push = (
    name: string, party: string | null, state: string | null,
    tds: string | null, partyId: string | null = null,
  ) => {
    if (!name || seen.has(name)) return
    seen.add(name)
    const o = orphans.get(name)
    out.push({
      name,
      party,
      party_id: partyId,
      state,
      tds: o ? o.tds : tds,
      tan: o?.tan ?? null,
      orphan: o != null,
      covered_by: covered?.[name] ?? null,
    })
  }

  if (scope === 'orphans') {
    for (const o of oneSided) push(o.name, null, null, o.tds)
  } else {
    for (const p of data.parties) {
      const names = side === '26as' ? p.names_26as : p.names_books
      const tds = side === '26as' ? p.tds_26as : p.tds_books
      for (const n of names) push(n, p.party, p.state, tds, p.party_id)
    }
    // a one-sided name the party projection does not carry is still a real
    // name in the run: listing it under "all" and not under "orphans" would
    // make the wider view the narrower one
    for (const o of oneSided) push(o.name, null, null, o.tds)
  }

  const q = query.trim().toUpperCase()
  const filtered = q ? out.filter((o) => o.name.toUpperCase().includes(q)) : out

  // A name already in an alias group sinks below everything actionable, under
  // BOTH orderings. It stays pickable — an alias can be superseded — but the
  // biggest names are exactly the mapped ones, so ranking them by rupees put a
  // wall of already-handled text at the head of both columns. Still listed,
  // never hidden: the alias applies from the NEXT run, so the name really is
  // one-sided here and saying otherwise would be a lie.
  const done = (o: PickerOption) => (o.covered_by ? 1 : 0)

  // The un-picked orderings tier the list: unmatched orphans, then the
  // already-matched parties, then the already-mapped tail. Ranked order
  // deliberately does NOT tier — the best candidate often lives in a matched
  // party, and that is exactly when it must be allowed to win.
  const tier = (o: PickerOption) => (o.covered_by ? 2 : o.orphan ? 0 : 1)

  // Nothing picked yet, two ways to ask the question. A-Z answers "are any of
  // these the same company twice" — one deductor misspelt sits beside its twin,
  // which money order files 60 rows apart. Money answers "where is the work
  // left". Once a name IS picked the question changes to "which of these is
  // that company", and neither ordering answers it — the ranking does.
  if (!ranked) {
    if (sort === 'name') {
      return filtered.sort((a, b) =>
        tier(a) - tier(b)
        || a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
    }
    return filtered.sort(
      (a, b) => tier(a) - tier(b) || Number(b.tds ?? 0) - Number(a.tds ?? 0),
    )
  }
  for (const o of filtered) {
    const r = ranked[o.name]
    if (r) {
      o.band = r.band
      o.evidence = r.evidence
      o.distinctive = r.distinctive
      o.initials = r.initials ?? null
      o.amount_hits = r.amount_hits ?? null
      // corroboration participates in the ORDER, not the band: the band says
      // what the NAMES show, and money agreeing is a different fact — but
      // evidence that sorts to the bottom is evidence the reader never meets
      o.rank = r.distinctive + corroborationBoost(r)
    }
  }
  // A name the ranking does not mention still appears, at the bottom. Picking
  // one name must not hide the others.
  return filtered.sort(
    (a, b) =>
      done(a) - done(b)
      || (b.rank ?? b.distinctive ?? -1) - (a.rank ?? a.distinctive ?? -1)
      || Number(b.tds ?? 0) - Number(a.tds ?? 0),
  )
}

/** Where the already-mapped block begins, or -1 if there is not one.
 *
 *  Two blocks are only legible if the list says where one ends, and the count
 *  on that divider is the only place the "already mapped" number appears. It
 *  declines to answer unless the mapped rows are a clean tail — a divider
 *  drawn at the first covered row in an interleaved list would label open
 *  names as mapped, which is worse than no divider.
 */
export function mappedFrom(options: Pick<PickerOption, 'covered_by'>[]): number {
  const first = options.findIndex((o) => o.covered_by)
  if (first < 0) return -1
  return options.slice(first).every((o) => o.covered_by) ? first : -1
}

/** Where the already-matched block begins, or -1 if there is not one.
 *
 *  The matched divider's sibling of `mappedFrom`, under the same honesty rule:
 *  it answers only when the list is cleanly [unmatched orphans] → [matched
 *  parties] → [mapped tail], which is what the un-picked orderings produce.
 *  Ranked order interleaves the blocks, and a divider drawn into an
 *  interleaved list would label unmatched names as matched, so it declines
 *  rather than guesses.
 */
export function matchedFrom(
  options: Pick<PickerOption, 'orphan' | 'covered_by'>[],
): number {
  const first = options.findIndex((o) => !o.orphan && !o.covered_by)
  if (first < 0) return -1
  if (options.slice(0, first).some((o) => o.covered_by)) return -1
  let inCovered = false
  for (const o of options.slice(first)) {
    if (o.covered_by) {
      inCovered = true
    } else if (o.orphan || inCovered) {
      return -1
    }
  }
  return first
}

/** Does this row belong on Reconciliation rather than here?
 *
 *  True only for a name whose party HAS both sides — a one-sided name is a
 *  naming question, and sending that reader away would send them from the one
 *  control that answers it.
 */
export function needsReconciliation(o: {
  party?: string | null
  party_id?: string | null
  state?: string | null
  orphan?: boolean
}): boolean {
  return !o.orphan && !!o.party_id && !!o.state && o.state !== 'reconciled'
}

/** The party drill-through's path, or null when it cannot be addressed.
 *
 *  Pure, because getting a URL wrong is not the kind of thing a type checker
 *  notices (the same reason `withRunParam` is). A half-built path would 404
 *  or, worse, land the reader on another client's run.
 */
export function partyPath(
  clientId: string | undefined,
  periodId: string | undefined,
  partyId: string | null | undefined,
  run: string | null,
): string | null {
  if (!clientId || !periodId || !partyId) return null
  return withRunParam(
    `/w/${clientId}/${periodId}/reconciliation/parties/${partyId}`, run,
  )
}

/** Why a name carries the `in <group>` badge, told correctly for the two
 *  different names that can carry it: what stays true for both is that the
 *  name is spoken for and that picking it again replaces the mapping. */
export function coveredLabel(
  o: { covered_by?: string | null; orphan?: boolean },
): string {
  return [
    `Already in "${o.covered_by}".`,
    o.orphan
      ? 'The alias applies from the next run, so this name still reads as'
        + ' one-sided here.'
      : 'Its party already has both sides.',
    'Pick it again to widen or replace that mapping — the preview says what'
    + ' would be superseded before anything is created.',
  ].join(' ')
}

const BAND_TONE: Record<string, 'green' | 'blue' | 'gray'> = {
  strong: 'green',
  likely: 'blue',
  weak: 'gray',
}

const STATE_TONE: Record<string, 'green' | 'yellow' | 'orange'> = {
  reconciled: 'green',
  pending: 'yellow',
  open: 'orange',
}

/** The clause every one of these badges carries, because the badge sits on a
 *  control whose whole purpose is naming. */
const ABOUT_ENTRIES =
  'This is the party’s reconciliation — about its entries and amounts, '
  + 'not its names. Naming is what this screen changes.'

const STATE_BADGE: Record<string, { label: string; title: string }> = {
  reconciled: {
    label: 'reconciled',
    title: `Its entries and amounts agree. ${ABOUT_ENTRIES}`,
  },
  pending: {
    label: 'awaiting approval',
    title: 'Every difference is matched or explained, and at least one '
      + `resolution is still waiting for approval. ${ABOUT_ENTRIES}`,
  },
  open: {
    label: 'unreconciled',
    title: 'At least one difference is neither matched nor explained. '
      + ABOUT_ENTRIES,
  },
}

/** The party's reconciliation state, worded so it cannot be read as a mapping
 *  state — `open` becomes `unreconciled`, a word that names the other
 *  question, and every state carries the sentence saying which question that
 *  is. An unrecognised state is passed through rather than dropped: a badge
 *  that silently vanishes is the failure that takes longest to notice. */
export function stateBadge(
  state: string | null | undefined,
): { label: string; title: string; color: string } | null {
  if (!state) return null
  const b = STATE_BADGE[state]
  return {
    label: b?.label ?? state,
    title: b?.title ?? `Party state "${state}". ${ABOUT_ENTRIES}`,
    color: STATE_TONE[state] ?? 'gray',
  }
}

function DividerLine({ label }: { label: string }) {
  return (
    <div className="my-1 flex items-center gap-2">
      <span className="h-px flex-1 bg-border" />
      <span className="whitespace-normal text-center text-[10px] text-muted-foreground">{label}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

function ScopeToggle<T extends string>({ value, onChange, options }: {
  value: T
  onChange: (v: T) => void
  options: { label: string; value: T }[]
}) {
  return (
    <span className="inline-flex shrink-0 overflow-hidden rounded-md border border-border text-xs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`px-2 py-0.5 ${value === o.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </span>
  )
}

/** One column of names: scope toggle, filter, and the list.
 *
 *  Exported because the Name mapping page builds its matching surface out of
 *  two of these — a second copy of this list would drift from this one the
 *  first time either changed.
 *
 *  `mode` is the only difference between the two uses. `single` is the primary
 *  column of that workbench: the ranking is computed against ONE name, so
 *  letting the reader pick three would average three companies into an answer
 *  about none of them.
 *
 *  `scopeLock` fixes the column to its own one-sided names — the primary
 *  column of a one-sided section already IS that scope, and a toggle that can
 *  only ever be wrong is worse than no toggle.
 */
export function NameColumn({
  title,
  side,
  data,
  selected,
  onToggle,
  ranked,
  against,
  mode = 'multi',
  scopeLock,
  defaultScope = 'orphans',
  defaultSort = 'value',
}: {
  title: string
  side: Side
  data: AliasCandidates | undefined
  selected: string[]
  onToggle: (name: string) => void
  /** likeness to the name picked on the other side, when there is one */
  ranked?: Record<string, Ranking>
  against?: string
  mode?: 'single' | 'multi'
  scopeLock?: Scope
  /** Which scope the toggle starts on. The counterpart column opens on ALL
   *  parties: measured on the predecessor's run 34, the best candidate lived
   *  in an already-matched party more than twice as often as in the unmatched
   *  list — Unmatched-first hid the likelier answer more often than it showed
   *  it. */
  defaultScope?: Scope
  defaultSort?: Sort
}) {
  const [scope, setScope] = useState<Scope>(defaultScope)
  const [sort, setSort] = useState<Sort>(defaultSort)
  const [query, setQuery] = useState('')
  // the workspace this column is being read inside, for the one link it can
  // offer. Absent outside a workspace route, and `partyPath` declines rather
  // than building half a URL.
  const { clientId, periodId } = useParams()
  const [search] = useSearchParams()
  const run = search.get('run')
  const eff = scopeLock ?? scope
  const options = useMemo(
    () => sideOptions(data, side, eff, query, ranked, sort),
    [data, side, eff, query, ranked, sort],
  )
  const divider = mappedFrom(options)
  // the matched-parties divider: in All-parties scope the list holds
  // companies that already have both sides — say so where their block starts,
  // or the honest one-sided lists read as lying
  const matchedAt = matchedFrom(options)
  const matchedCount = matchedAt < 0
    ? 0
    : (divider >= 0 ? divider : options.length) - matchedAt

  return (
    <div className="min-w-[300px] flex-1 space-y-2">
      <div className="flex flex-nowrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {!scopeLock && (
          <ScopeToggle
            value={scope}
            onChange={setScope}
            options={[
              { label: 'Unmatched', value: 'orphans' },
              { label: 'All parties', value: 'all' },
            ]}
          />
        )}
      </div>
      <div className="flex flex-nowrap items-center gap-2">
        <input
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
          placeholder="Filter names"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {/* Hidden once a name is picked: the far column is then ordered by
            likeness, and offering a sort that does not apply would be a
            control that silently does nothing. */}
        {!ranked && (
          <ScopeToggle
            value={sort}
            onChange={setSort}
            options={[
              { label: 'A–Z', value: 'name' },
              { label: 'Money', value: 'value' },
            ]}
          />
        )}
      </div>
      {against && (
        <p className="text-xs text-muted-foreground">
          Ranked against <b>{against}</b> — most like it first. Everything is
          still listed.
        </p>
      )}
      <div className="rounded-lg border border-border p-2">
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {options.length === 0 && (
            <p className="p-2 text-xs text-muted-foreground">Nothing to show.</p>
          )}
          {options.map((o, i) => (
            <Fragment key={o.name}>
              {i === matchedAt && matchedCount > 0 && (
                <DividerLine
                  label={`${matchedCount} already matched — tick one only to fold this name into its party`}
                />
              )}
              {i === divider && (
                <DividerLine
                  label={`${options.length - divider} already mapped — still pickable, to widen or replace`}
                />
              )}
              {/* A name already in an alias stays PICKABLE: an existing group
                  may not reach far enough, and picking the name again with
                  the right counterparts supersedes and retires it. The badge
                  says which group owns it, and the plan preview warns before
                  anything is replaced. */}
              <label className="flex cursor-pointer flex-nowrap items-center gap-1.5">
                <input
                  type={mode === 'single' ? 'radio' : 'checkbox'}
                  checked={selected.includes(o.name)}
                  onChange={() => onToggle(o.name)}
                  onClick={mode === 'single' && selected.includes(o.name)
                    ? () => onToggle(o.name) : undefined}
                />
                <span className="text-xs text-foreground">{o.name}</span>
                {o.covered_by && (
                  <Chip tone="teal" title={coveredLabel(o)} className="max-w-36 overflow-hidden">
                    <span className="truncate">in {o.covered_by}</span>
                  </Chip>
                )}
                {o.band && (
                  <Chip tone={BAND_TONE[o.band] ?? 'gray'}
                    title={o.evidence?.length
                      ? `shares ${o.evidence.join(', ')}`
                      : 'nothing identifying in common'}>
                    {o.band}
                  </Chip>
                )}
                {/* the two corroborations, each stating its fact: a short form
                    spelling the other name, and rare amounts tying — how the
                    right answer surfaces when every name measure scores low */}
                {o.initials && (
                  <Chip tone="violet" title={o.initials}>short form</Chip>
                )}
                {o.amount_hits && (
                  <Chip tone="teal"
                    title={`${o.amount_hits.n} entr${o.amount_hits.n === 1 ? 'y' : 'ies'} tie: `
                      + `${o.amount_hits.amounts.join(', ')} · ₹${o.amount_hits.money} in all`}>
                    ₹ ties ×{o.amount_hits.n}
                  </Chip>
                )}
                {/* the party's RECONCILIATION, named as such — the badge IS
                    the route: the sentence lives in the tooltip, the badge
                    carries the link, so the row keeps its single line */}
                {(() => {
                  const b = stateBadge(o.state)
                  if (!b) return null
                  const to = needsReconciliation(o)
                    ? partyPath(clientId, periodId, o.party_id, run)
                    : null
                  const chip = (
                    <Chip tone={b.color as 'green' | 'yellow' | 'orange' | 'gray'}
                      title={to ? `${b.title} Open it in Reconciliation.` : b.title}>
                      {b.label}{to ? ' →' : ''}
                    </Chip>
                  )
                  return to
                    ? <Link to={to} onClick={(e) => e.stopPropagation()}>{chip}</Link>
                    : chip
                })()}
                {o.tds != null && Number(o.tds) > 0 && (
                  <span className="font-mono text-xs text-muted-foreground">
                    ₹{Number(o.tds).toLocaleString('en-IN')}
                  </span>
                )}
              </label>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}

/** What this alias would close, and what it would leave open.
 *
 *  ONE component rendered in two places — the gap band above the columns and
 *  the plan preview beside Create. It states the residual, not just `closes`:
 *  `closes` is the min of the two shortfalls, so alone it reads "₹96,000
 *  closes" while money just ticked stays unmatched.
 */
export function ClosureLine({ plan, fz = 'text-sm' }: { plan: AliasPlan; fz?: string }) {
  const rupees = (v: string | undefined) => `₹${Number(v ?? 0).toLocaleString('en-IN')}`
  const left26 = Number(plan.open_26as ?? 0) - Number(plan.closes ?? 0)
  const leftBk = Number(plan.open_books ?? 0) - Number(plan.closes ?? 0)
  const rest = [
    left26 > 0 ? `${rupees(String(left26))} of the 26AS money` : null,
    leftBk > 0 ? `${rupees(String(leftBk))} of the books money` : null,
  ].filter(Boolean)
  return (
    <p className={`${fz} text-foreground`}>
      Would close{' '}
      <span className="font-mono font-bold">{rupees(plan.closes)}</span>
      {Number(plan.closes ?? 0) === 0 ? (
        <span className="text-xs text-muted-foreground"> — these parties are not short against each other</span>
      ) : rest.length ? (
        <span className="text-xs text-muted-foreground"> — {rest.join(' and ')} would stay open</span>
      ) : (
        <span className="text-xs text-muted-foreground"> — nothing would be left open on either side</span>
      )}
    </p>
  )
}

/** What creating this alias would cover, before it is created.
 *
 *  NOT optional detail. `create_group_from_run` records the reach signature at
 *  creation instead of sending the group round a second approval loop, and its
 *  reason is precisely this panel: the author read every covered name before
 *  they clicked, so they have signed for exactly that list. Any surface that
 *  can create an alias must render this first.
 */
export function AliasPlanPreview({ plan }: { plan: AliasPlan }) {
  const p = plan
  return (
    <div className="space-y-1.5 rounded-lg border border-border p-3">
      <p className="text-sm font-semibold text-foreground">{p.label}</p>
      <p className="text-xs text-muted-foreground">
        Covers {p.covers_26as.length} 26AS name(s) and {p.covers_books.length} books name(s).
      </p>
      {/* a mapping that closes a real difference must not look identical to
          one that only tidies a name */}
      <ClosureLine plan={p} />
      {(p.added_26as.length > 0 || p.added_books.length > 0) && (
        <p className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
          Also including {[...p.added_26as, ...p.added_books].join(', ')} — these names
          already belong to the same parties, and an alias that left them out would take
          their counterparts away.
        </p>
      )}
      {p.no_op && (
        /* the same string on both sides because the name was one-sided in
           BOTH columns: creating it, approving it and re-running changed
           nothing, twice, and nothing said why. Now something says why. */
        <p className="rounded-md border border-orange-200 bg-orange-50 p-2 text-xs text-orange-900">
          These names already belong to one party — {p.parties.join(', ')} — so the engine
          has joined them itself and this alias will not change any figure. If that party
          is still open, the difference is between its <b>entries</b>, not between its
          names: work it on the Reconciliation tab. You can still create this to freeze
          the mapping for future runs.
        </p>
      )}
      {p.supersedes.length > 0 && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          This replaces the existing alias {p.supersedes.map((s) => s.label).join(', ')},
          which will be retired. Past runs keep their snapshot.
        </p>
      )}
      {/* The regexes deliberately do NOT render here. They are the
          implementation of the mapping, not the decision: the preview states
          the names covered and the money closed, which is what the person
          pressing Create is agreeing to. The pattern stays visible in exactly
          one place — the Rules tab — where auditing patterns is the reader's
          actual task. */}
    </div>
  )
}

/** Build an alias against a run.
 *
 *  The regexes are not composed here. The server expands the selection to the
 *  whole of every party it touches, because an alias group REPLACES those
 *  parties in the engine — leave out a name one of them already owns and that
 *  name loses its counterpart on the next run. This screen shows what the
 *  expansion added rather than hiding it. */
export function AliasPicker({
  runId,
  clientId,
  seedParty,
  embedded,
}: {
  runId: string
  clientId?: string
  /** inside the merged Name mapping screen, which supplies its own heading */
  embedded?: boolean
  /** Preselect everything one party already owns. Used from the party
   *  drill-through, where the question is always "is one of the run's other
   *  names this party too?" — starting from a blank picker would make the
   *  reconciler re-find the party they are already looking at. */
  seedParty?: string
}) {
  const [n26, setN26] = useState<string[]>([])
  const [nbk, setNbk] = useState<string[]>([])
  const [seeded, setSeeded] = useState(false)
  // Pick on one side, the other ranks against it. One name only: ranking a
  // column against three different companies at once would average them into
  // an answer about none of them.
  const against =
    n26.length === 1 && nbk.length === 0
      ? ({ name: n26[0], side: '26as' } as const)
      : nbk.length === 1 && n26.length === 0
        ? ({ name: nbk[0], side: 'books' } as const)
        : undefined
  const candidates = useAliasCandidates(runId, against)
  const ranked = candidates.data?.against === against?.name
    ? (candidates.data?.ranked ?? undefined)
    : undefined
  const seed = seedParty
    ? candidates.data?.parties.find((p) => p.party === seedParty)
    : undefined
  if (seed && !seeded) {
    setSeeded(true)
    setN26(seed.names_26as)
    setNbk(seed.names_books)
  }
  const sel = { names_26as: n26, names_books: nbk }
  const plan = useAliasPlan(runId, sel)
  const create = useCreateAliasFromRun(runId, clientId ?? '')

  const toggle = (list: string[], set: (v: string[]) => void) => (name: string) =>
    set(list.includes(name) ? list.filter((x) => x !== name) : [...list, name])

  const ready = n26.length > 0 && nbk.length > 0
  const p = plan.data

  return (
    <div className="space-y-3">
      {!embedded && <h2 className="text-lg font-semibold text-foreground">Build an alias</h2>}
      <p className="text-sm text-muted-foreground">
        Pick a name on one side and the other side re-orders by how like it each name is. Switch
        a column to <b>All parties</b> to join an unmatched name to a party that already
        matched — the alias that actually closes a shortfall.
      </p>
      {candidates.isPending ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
      ) : candidates.isError ? (
        <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          Could not load this run&rsquo;s parties: {String(candidates.error)}
        </p>
      ) : (
        <div className="flex flex-wrap items-start gap-4">
          <NameColumn
            title="26AS deductor names"
            side="26as"
            data={candidates.data}
            selected={n26}
            onToggle={toggle(n26, setN26)}
            ranked={against?.side === 'books' ? ranked : undefined}
            against={against?.side === 'books' ? against.name : undefined}
          />
          <NameColumn
            title="Books party names"
            side="books"
            data={candidates.data}
            selected={nbk}
            onToggle={toggle(nbk, setNbk)}
            ranked={against?.side === '26as' ? ranked : undefined}
            against={against?.side === '26as' ? against.name : undefined}
          />
        </div>
      )}

      {ready && p && <AliasPlanPreview plan={p} />}

      <div className="flex items-center gap-2">
        <Btn
          kind="primary"
          disabled={!ready}
          loading={create.isPending}
          onClick={() =>
            create.mutate(sel, {
              onSuccess: (r) => {
                showToast({
                  color: 'green',
                  title: `Alias created: ${r.group.label}. It applies from the next run.`,
                })
                setN26([])
                setNbk([])
              },
              onError: (e) =>
                showToast({
                  color: 'red',
                  title: 'Could not create the alias',
                  message: e instanceof ApiError ? e.message : String(e),
                }),
            })
          }
        >
          Create alias
        </Btn>
        {(n26.length > 0 || nbk.length > 0) && (
          <Btn
            kind="subtle"
            onClick={() => {
              setN26([])
              setNbk([])
            }}
          >
            Clear
          </Btn>
        )}
      </div>
    </div>
  )
}
